import {
  Actions,
  Dispatchers,
  toAsyncFetched,
} from '@client/new/helpers/types';
import { toast } from 'react-hot-toast';
import { getDispatchers } from '@client/new/helpers/types-utils';
import { trackSessionEvent } from '@services/session-tracker/sessionTracker';
import { useDiscountDetailsModel } from '../model/DiscountDetails.model';
import {
  DiscountsVM,
  DiscountVMActionMap,
  useDiscountsVM,
} from './discounts.viewModel';
import { ListRow } from '../view/components/GridList';
import { pickObject } from '@client/new/helpers/general';
import {
  toBackURL,
  toPerformanceCards,
  toStatusTitle,
  toUserRows,
} from './discountDetails.mappers';
import { Discount } from '../model/Discounts.model';
import { getCSV } from '../model/csv';

export type DiscountDetailsVM = {
  userRows: Array<ListRow>;
  performanceCards: Array<ListRow>;
  title: string;
  checkoutURL: string;
  backURL: string;
  statusTitle: string;
  statusType: Discount['status'];
  showDeactivateButton: boolean;
} & Dispatchers<DiscountDetailsVMActionMap> &
  Pick<DiscountsVM, 'deactivationMode'>;

type DiscountDetailsVMActionMap = Pick<
  DiscountVMActionMap,
  'CloseDeactivating' | 'FinishDeactivation'
> & { CopyText: string; StartDeactivateDiscount: null; ExportCSV: null };

export type DiscountDetailsAction = Actions<DiscountDetailsVMActionMap>;

export const useDiscountDetailsVM = (discountId: string) => {
  const asyncModel = useDiscountDetailsModel(discountId);
  const discountsVM = useDiscountsVM();

  const _dispatch = (a: DiscountDetailsAction) => {
    if (discountsVM.type !== 'fetched') return;
    trackSessionEvent('ViewAction', a);
    switch (a.type) {
      case 'CopyText': {
        navigator.clipboard.writeText(a.payload);
        toast.success('Copied');
        return;
      }
      case 'StartDeactivateDiscount':
        return discountsVM.value.dispatch(
          'StartDeactivateDiscount',
          model.discount.discountId,
        );
      case 'ExportCSV':
        return getCSV(model.users, model.discount.name + '.csv');

      case 'CloseDeactivating':
      case 'FinishDeactivation':
        return discountsVM.value.dispatch(a.type);
    }
  };

  const dispatchers = getDispatchers<DiscountDetailsVMActionMap>(_dispatch);

  if (asyncModel.type !== 'fetched') return asyncModel;
  if (discountsVM.type !== 'fetched') return discountsVM;

  const model = asyncModel.value;

  return toAsyncFetched<DiscountDetailsVM>({
    showDeactivateButton: model.discount.status === 'Active',
    userRows: toUserRows(model.users),
    backURL: toBackURL(),
    ...dispatchers,
    checkoutURL: model.discount.url || '',
    performanceCards: toPerformanceCards(model),
    title: model.discount.name,
    statusTitle: toStatusTitle(model.discount.product),
    statusType: model.discount.status,
    ...pickObject(discountsVM.value, ['deactivationMode']),
  });
};
